import type { IdentityProviderType } from '@/types/identity-provider';
import type { FieldDescriptor } from './connectorFields';

/**
 * Per-provider connection fields, with first-run guidance.
 *
 * The hints are the "what do I put here and why" layer: they say where the
 * value is found in the provider's own console and what permission it needs,
 * because a wrong-but-well-formed value fails at sync time with a vendor error
 * rather than at save time. They are also compiled into the Ask AI help corpus,
 * so keep them factual and grounded in what the adapter actually calls.
 */
export const IDENTITY_PROVIDER_FIELDS: Record<IdentityProviderType, FieldDescriptor[]> = {
  azure_entra_id: [
    {
      name: 'tenant_id', label: 'Tenant ID', type: 'text',
      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true, secret: false,
      hint: 'Entra admin center, Overview, "Tenant ID". A GUID, not your domain name.',
    },
    {
      name: 'client_id', label: 'Client ID', type: 'text',
      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true, secret: false,
      hint: 'The Application (client) ID of the app registration you created for Gateco, not the object ID. Gateco reads users and groups through Microsoft Graph, so the app needs three application permissions with admin consent granted: User.Read.All, Group.Read.All and GroupMember.Read.All. The third is required because Gateco reads the members of each group to map principals to groups; without it the sync stops with a 403 from Microsoft Graph.',
    },
    {
      name: 'client_secret', label: 'Client Secret', type: 'password',
      placeholder: '', required: true, secret: true,
      hint: 'The secret VALUE shown once when you create it, not the Secret ID. If you navigated away before copying it, create a new one. Note its expiry: sync starts failing on the day it lapses.',
    },
  ],
  aws_iam: [
    {
      name: 'identity_store_id', label: 'Identity Store ID', type: 'text',
      placeholder: 'd-1234567890', required: true, secret: false,
      hint: 'IAM Identity Center, Settings, "Identity store ID". Begins with d- and is not your AWS account number.',
    },
    {
      name: 'aws_access_key_id', label: 'Access Key ID', type: 'text',
      placeholder: 'AKIAIOSFODNN7EXAMPLE', required: true, secret: false,
      hint: 'Credentials for a principal allowed to read the Identity Store: identitystore:ListUsers, identitystore:ListGroups and identitystore:ListGroupMembershipsForMember. That last action is the per-member form and is distinct from ListGroupMemberships, so a policy granting only the latter still fails. Read-only is enough; Gateco never writes to your directory, and any missing action surfaces as an AWS AccessDenied rather than a partial sync.',
    },
    {
      name: 'aws_secret_access_key', label: 'Secret Access Key', type: 'password',
      placeholder: '', required: true, secret: true,
      hint: 'Shown once when the access key is created. If you no longer have it, rotate the key rather than guessing.',
    },
    {
      name: 'region', label: 'Region', type: 'text',
      placeholder: 'us-east-1', required: true, secret: false,
      hint: 'The region your Identity Center instance is in, which is often not the region you work in day to day. If it is wrong, the identity store is not found in that region and the sync fails with an AWS error.',
    },
  ],
  gcp: [
    {
      name: 'domain', label: 'Workspace Domain', type: 'text',
      placeholder: 'company.com', required: true, secret: false,
      hint: 'Your Google Workspace primary domain, for example company.com. Not a project id and not a URL.',
    },
    {
      name: 'admin_email', label: 'Admin Email (delegated)', type: 'text',
      placeholder: 'admin@company.com', required: true, secret: false,
      hint: 'A real super-admin user the service account impersonates. The Admin SDK refuses to list a directory for a service account acting as itself, so this cannot be left blank or set to the service account address.',
    },
    {
      name: 'service_account_json', label: 'Service Account JSON', type: 'password',
      placeholder: '{"type":"service_account",...}', required: true, secret: true,
      hint: 'The whole key file, pasted as JSON. The service account needs domain-wide delegation authorised in the Admin console for three read-only scopes: admin.directory.user.readonly, admin.directory.group.readonly and admin.directory.group.member.readonly. Delegation is the step most often missed. Missing the user or group scope stops the sync with a 403 from Google. Missing only the member scope is worse, because it does not stop anything: groups import with no members, and every group-based policy then matches nobody.',
    },
  ],
  okta: [
    {
      name: 'domain', label: 'Okta Domain', type: 'text',
      placeholder: 'dev-123456.okta.com', required: true, secret: false,
      hint: 'Your Okta org host with no scheme and no trailing path, for example dev-123456.okta.com. Use the admin host only if that is the org you are syncing.',
    },
    {
      name: 'api_token', label: 'API Token', type: 'password',
      placeholder: '', required: true, secret: true,
      hint: 'Security, API, Tokens, Create token. Gateco sends it as an SSWS token to the Users and Groups endpoints and only ever reads. Okta tokens expire after 30 days of no use, which shows up as a sync that worked for a month and then stopped. If you only want Okta to push users into Gateco rather than Gateco pulling them, use SCIM provisioning mode instead and no API token is needed.',
    },
  ],
};
